import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import WhatsAppButton from "./ui/whatsapp-button";
import CompanyDeckButton from "./ui/company-deck-button";
import PyCrayForgeYoutubeButton from "./ui/pycray-forge-youtube-button";
import { navigateToHomeSection } from "../utils/scrollToSection";
import pycrayLogo from "../asset/logo/pycray.png";

const navLinks = [
  { label: "Home", route: "/" },
  { label: "AI Use Cases", route: "/use-cases" },
  { label: "Forge", route: "/forge" },
  { label: "Sessions", route: "/sessions" },
];

const moreLinks = [
  { label: "ReviewLoop", href: "https://www.reviewloop.in/" },
  { label: "About Us", route: "/about-us" },
  { label: "Careers", route: "/careers" },
];

export const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const headerRef = useRef(null);
  const moreMenuRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(56);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 12);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const updateHeight = () => setHeaderHeight(el.offsetHeight);
    updateHeight();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateHeight);
      return () => window.removeEventListener("resize", updateHeight);
    }

    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);
    window.addEventListener("resize", updateHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, []);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  }, [location]);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMoreOpen) return;

    const handleClickOutside = (event) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setIsMoreOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMoreOpen]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add("nav-menu-open");
    } else {
      document.body.classList.remove("nav-menu-open");
    }
    return () => document.body.classList.remove("nav-menu-open");
  }, [isMenuOpen]);

  const handleNavClick = (link) => {
    setIsMenuOpen(false);
    setIsMoreOpen(false);
    if (link.href) {
      window.open(link.href, "_blank", "noopener,noreferrer");
      return;
    }
    if (link.route) {
      navigate(link.route);
      window.scrollTo(0, 0);
      return;
    }
    navigateToHomeSection(navigate, link.sectionId);
  };

  const headerClass = isScrolled
    ? "saas-glass shadow-sm"
    : "bg-white/70 backdrop-blur-md border-b border-slate-100/80";

  const mobileMenu =
    typeof document !== "undefined"
      ? createPortal(
          <>
            <div
              className={`fixed left-0 right-0 bottom-0 bg-slate-900/20 backdrop-blur-sm md:hidden transition-opacity duration-300 z-[200] ${
                isMenuOpen
                  ? "opacity-100 visible"
                  : "opacity-0 invisible pointer-events-none"
              }`}
              style={{ top: headerHeight }}
              onClick={() => setIsMenuOpen(false)}
              aria-hidden={!isMenuOpen}
            />

            <div
              id="mobile-nav-menu"
              role="dialog"
              aria-modal="true"
              aria-label="Navigation menu"
              className={`fixed right-0 bottom-0 w-full max-w-[320px] bg-white md:hidden shadow-2xl transition-transform duration-300 ease-in-out z-[201] border-l border-slate-100 ${
                isMenuOpen
                  ? "translate-x-0 pointer-events-auto"
                  : "translate-x-full pointer-events-none"
              }`}
              style={{ top: headerHeight }}
            >
              <div
                className="flex flex-col px-5 py-6 overflow-y-auto"
                style={{ height: `calc(100dvh - ${headerHeight}px)` }}
              >
                <ul className="space-y-1 flex-1">
                  {navLinks.map((link) => (
                    <li key={link.label}>
                      <button
                        type="button"
                        className="w-full text-left py-3.5 px-3 rounded-xl text-slate-700 text-base font-medium hover:bg-slate-50 transition-colors"
                        onClick={() => handleNavClick(link)}
                      >
                        {link.label}
                      </button>
                    </li>
                  ))}
                  <li className="pt-2">
                    <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">
                      More
                    </p>
                    {moreLinks.map((link) => (
                      <button
                        key={link.label}
                        type="button"
                        className="w-full text-left py-3.5 px-3 rounded-xl text-slate-700 text-base font-medium hover:bg-slate-50 transition-colors"
                        onClick={() => handleNavClick(link)}
                      >
                        {link.label}
                      </button>
                    ))}
                  </li>
                </ul>
                <div className="pt-6 mt-auto border-t border-slate-100 shrink-0 flex flex-col gap-3">
                  <CompanyDeckButton className="w-full" />
                  <PyCrayForgeYoutubeButton />
                  <WhatsAppButton label="Chat with us" className="w-full" />
                </div>
              </div>
            </div>
          </>,
          document.body
        )
      : null;

  return (
    <>
      <header
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 transition-all duration-300 safe-top ${headerClass} ${
          isMenuOpen ? "z-[202]" : "z-50"
        }`}
      >
        <nav className="page-container">
          <div className="flex items-center justify-between h-14 sm:h-[72px] gap-2 sm:gap-4 min-w-0">
            <Link
              to="/"
              onClick={() => {
                setIsMenuOpen(false);
                window.scrollTo(0, 0);
              }}
              className="flex-shrink-0 min-w-0 hover:opacity-90 transition-opacity"
              aria-label="PyCray Technologies home"
            >
              <img
                src={pycrayLogo}
                alt="PyCray Technologies"
                className="h-8 sm:h-10 w-auto max-w-[110px] sm:max-w-[140px] object-contain object-left"
              />
            </Link>

            <div className="hidden md:flex flex-1 justify-center min-w-0">
              <ul className="flex flex-wrap justify-center gap-x-8 items-center">
                {navLinks.map((link) => (
                  <li key={link.label}>
                    <button
                      type="button"
                      onClick={() => handleNavClick(link)}
                      className="text-slate-600 font-medium text-sm hover:text-slate-900 transition-colors whitespace-nowrap"
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
                <li className="relative" ref={moreMenuRef}>
                  <button
                    type="button"
                    onClick={() => setIsMoreOpen((open) => !open)}
                    className="inline-flex items-center gap-1 text-slate-600 font-medium text-sm hover:text-slate-900 transition-colors whitespace-nowrap"
                    aria-expanded={isMoreOpen}
                    aria-haspopup="true"
                  >
                    More
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${
                        isMoreOpen ? "rotate-180" : ""
                      }`}
                      aria-hidden
                    />
                  </button>
                  {isMoreOpen && (
                    <ul className="absolute top-full left-1/2 -translate-x-1/2 mt-3 min-w-[10.5rem] rounded-xl bg-white border border-slate-100 shadow-lg py-2 z-50">
                      {moreLinks.map((link) => (
                        <li key={link.label}>
                          <button
                            type="button"
                            onClick={() => handleNavClick(link)}
                            className="w-full text-left px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                          >
                            {link.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              </ul>
            </div>

            <div className="hidden md:flex items-center gap-2 shrink-0">
              <PyCrayForgeYoutubeButton />
              <CompanyDeckButton className="hidden xl:inline-flex" />
              <CompanyDeckButton shortLabel className="xl:hidden" />
              <WhatsAppButton label="Chat with us" />
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2 md:hidden shrink-0">
              <PyCrayForgeYoutubeButton compact />
              <CompanyDeckButton compact />
              <WhatsAppButton label="Chat with us" compact />
              <button
                type="button"
                onClick={() => setIsMenuOpen((open) => !open)}
                className="p-2 rounded-lg text-slate-700 hover:bg-slate-50 shrink-0"
                aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={isMenuOpen}
                aria-controls="mobile-nav-menu"
              >
                {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </nav>
      </header>

      {mobileMenu}
    </>
  );
};
